(() => {
  /* I18N-06 (#163): visible strings come from the page; the English literal is the fallback. */
  const wfStrings = (() => {
    try {
      const node = document.querySelector('script[type="application/json"][data-wf-i18n="bmi-calculator"]');
      const parsed = node ? JSON.parse(node.textContent) : null;
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch (error) {
      return {};
    }
  })();
  // Shopify's `t` HTML-escapes every translation whose key does not end in `_html` (French
  // "n'a" arrives as "n&#39;a"), so the page's values are decoded back to plain text.
  const wfDecode = (text) =>
    text.replace(/&(#x[0-9a-f]+|#\d+|amp|lt|gt|quot|apos);/gi, (entity, body) => {
      const named = { amp: '&', lt: '<', gt: '>', quot: '"', apos: "'" }[body.toLowerCase()];
      if (named) return named;
      const code = body[1] === 'x' || body[1] === 'X' ? parseInt(body.slice(2), 16) : parseInt(body.slice(1), 10);
      return code > 0 && code <= 0x10ffff ? String.fromCodePoint(code) : entity;
    });
  const tr = (key, fallback, values = {}) => {
    const text = typeof wfStrings[key] === 'string' && wfStrings[key] !== '' ? wfDecode(wfStrings[key]) : fallback;
    return Object.keys(values).reduce((out, name) => out.split(`{${name}}`).join(values[name]), text);
  };

  class SBmiCalculator extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('[data-bmi-form]');
      this.modal = this.querySelector('[data-bmi-modal]');
      this.closeButtons = this.querySelectorAll('[data-bmi-close]');
      this.breedSelect = this.querySelector('[data-breed-select]');

      this.resultTitle = this.querySelector('[data-bmi-result-title]');
      this.scoreEl = this.querySelector('[data-bmi-score]');
      this.resultText = this.querySelector('[data-bmi-result-text]');
      this.reportList = this.querySelector('[data-bmi-report]');
      this.recommendations = this.querySelector('[data-bmi-recommendations]');

      this.breedsData = {};

      this.onSubmit = this.onSubmit.bind(this);
      this.closeModal = this.closeModal.bind(this);
      this.onKeydown = this.onKeydown.bind(this);

      this.form?.addEventListener('submit', this.onSubmit);

      this.closeButtons.forEach((button) => {
        button.addEventListener('click', this.closeModal);
      });

      this.loadBreeds().then((data) => {
        this.breedsData = data || {};
        this.populateBreedDropdown();
      });

      this.loadJson(this.dataset.weightsUrl).then((data) => {
        this.weightRanges = (data && data.adultWeightKg) || {};
      });
    }

    disconnectedCallback() {
      this.form?.removeEventListener('submit', this.onSubmit);

      this.closeButtons.forEach((button) => {
        button.removeEventListener('click', this.closeModal);
      });

      document.removeEventListener('keydown', this.onKeydown);
    }

    async loadBreeds() {
      return this.loadJson(this.dataset.breedsUrl);
    }

    async loadJson(url) {
      if (!url) return {};

      try {
        const response = await fetch(url, { cache: 'force-cache' });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return await response.json();
      } catch (error) {
        console.warn('[BMI Calculator] Could not load JSON:', url, error);
        return {};
      }
    }

    populateBreedDropdown() {
      if (!this.breedSelect) return;

      const selectedValue = this.breedSelect.value;

      const breeds = Object.entries(this.breedsData || {})
        .flatMap(([size, items]) => {
          return (items || []).map((breed) => ({
            breed,
            size
          }));
        })
        .sort((a, b) =>
          String(a.breed || '').localeCompare(String(b.breed || ''), undefined, {
            sensitivity: 'base'
          })
        );

      /* I18N-06 (#163): the placeholder option is translated, and escaped because this is innerHTML. */
      this.breedSelect.innerHTML = [
        `<option value="">${this.escapeHtml(tr('select_breed', 'Select dog breed'))}</option>`,
        ...breeds.map((item) => {
          return `<option value="${this.escapeHtml(item.breed)}" data-size="${this.escapeHtml(item.size)}">${this.escapeHtml(item.breed)}</option>`;
        })
      ].join('');

      if (selectedValue) {
        this.breedSelect.value = selectedValue;
      }
    }

    onSubmit(event) {
      event.preventDefault();

      const formData = new FormData(this.form);

      const data = {
        breed: String(formData.get('breed') || '').trim(),
        age: Number(formData.get('age') || 0),
        weight: Number(formData.get('weight') || 0),
        height: Number(formData.get('height') || 0),
        length: Number(formData.get('length') || 0),
        activity: String(formData.get('activity') || ''),
        ribs: String(formData.get('ribs') || ''),
        waist: String(formData.get('waist') || ''),
        belly: String(formData.get('belly') || '')
      };

      const problem = this.validate(data);
      if (problem) {
        this.showError(problem);
        return;
      }
      this.showError('');

      const breedSize = this.getBreedSizeGroup(data.breed, this.breedsData);

      const result = this.calculateResult({
        ...data,
        breedSize
      });

      this.renderResult(result);
      this.openModal();
    }

    calculateResult(data) {
      const bmiIndex = this.calculateBmiIndex(data);

      let assessmentScore = 5;

      const ribScores = {
        very_visible: -3,
        easy_to_feel: -1,
        slightly_padded: 0,
        hard_to_feel: 2,
        not_feelable: 3
      };

      const waistScores = {
        very_tucked: -2,
        visible: 0,
        slight: 1,
        none: 2,
        rounded: 3
      };

      const bellyScores = {
        strong_tuck: -2,
        normal_tuck: 0,
        minimal_tuck: 1,
        straight: 2,
        hanging: 3
      };

      const activityScores = {
        low: 1,
        normal: 0,
        high: -1
      };

      assessmentScore += ribScores[data.ribs] || 0;
      assessmentScore += waistScores[data.waist] || 0;
      assessmentScore += bellyScores[data.belly] || 0;
      assessmentScore += activityScores[data.activity] || 0;
      assessmentScore += this.getBreedWeightAdjustment(data);
      /**
       * GAP3-03 (#219). This used to shift the verdict by up to two points on fixed bands of the
       * weight / (height x length) quotient - 30, 36, 76, 100 - identical for a Chihuahua and a
       * Great Dane. Two problems with that, and they compound.
       *
       * The bands are not validated against anything. The quotient is a surface density, so its
       * natural value rises with the size of the animal; one set of cut-offs cannot be right for
       * both ends of the range, and there is no source in this repository for what the cut-offs
       * should be at either end.
       *
       * And it was the only route by which weight reached the verdict, so with no upper bound on
       * the inputs, 1000 kg scored +2 and came back "Slightly overweight".
       *
       * Removed rather than re-tuned. What decides the verdict now is the rib, waist and belly
       * answers, which are the nine-point body condition scale a vet would actually use, and which
       * this component was already computing. The quotient is still shown, with its own scale
       * beside it, as information rather than as a judgement.
       *
       * WHAT WOULD BE NEEDED TO MAKE THE BREED COUNT, since the finding asks for it: a healthy
       * adult weight range per breed. The size groups already in `breedsData` will not serve. They
       * are built for fitting harnesses, and by weight they overlap almost completely - "medium"
       * runs from the Pug at 7 kg to the Poodle at 32 kg, "large" from the Border Collie at 17 kg
       * to the Labrador at 30 kg, and "extra-large" from the Greyhound at 33 kg to the Mastiff at
       * 80 kg. Banding weight on those groups would call a healthy 7 kg Pug underweight, which is
       * a different wrong answer wearing a more confident face. That table has to come from
       * someone who can stand behind it.
       */

      assessmentScore = Math.max(1, Math.min(9, Math.round(assessmentScore)));

      const content = this.getCategoryContent(assessmentScore);

      return {
        ...data,
        assessmentScore,
        bmiIndex,
        ...content,
        report: this.buildReport(data, assessmentScore, content.category, bmiIndex),
        recommendations: this.buildRecommendations(data, assessmentScore)
      };
    }

    calculateBmiIndex(data) {
      if (!data.weight || !data.height || !data.length) return '';

      const heightM = data.height / 100;
      const lengthM = data.length / 100;

      if (!heightM || !lengthM) return '';

      return (data.weight / (heightM * lengthM)).toFixed(1);
    }


    /**
     * Ranges a dog can actually be, not ranges a dog should be. Anything outside these is a typo
     * or a unit mix-up, and the honest response is to say so rather than to score it.
     */
    showError(message) {
      const el = this.querySelector('[data-bmi-error]');
      if (!el) return;
      el.textContent = message;
      el.hidden = !message;
    }

    /**
     * GAP3-03 (#219): what makes the breed count.
     *
     * `breeds-by-adult-weight.json` gives an adult reference range per breed, so a weight can be
     * read against the dog in front of the owner rather than against every dog at once. This is
     * what replaced the old fixed bands of 30/36/76/100, which were identical for a Chihuahua and
     * a Great Dane and were the reason changing the breed changed nothing.
     *
     * THREE THINGS IT REFUSES TO DO, each of which would make it wrong in a new way.
     *
     * It says nothing about a dog under eighteen months. The file's own usage notes are explicit
     * that these are adult ranges and not puppy growth targets, and a healthy five-month puppy is
     * legitimately below the adult minimum. Frightening the owner of a normal puppy would be a
     * worse failure than the one this fixes.
     *
     * It halves its effect on a medium-confidence entry. 157 of the 391 rows are reviewed
     * estimates for designer crosses, where parent size genuinely varies - the Goldendoodle range
     * is 7 to 41 kg, and it is right that such a breed almost never triggers this at all.
     *
     * And it never outranks the body condition answers. Those remain the nine-point scale a vet
     * would use; this is a cross-check on the number the owner typed, not a diagnosis.
     */
    /**
     * The same comparison in words, in kilograms rather than in an index nobody can check.
     */
    getBreedWeightNote(data) {
      /* I18N-06 (#163): each note is one whole translated sentence set; a cross-breed gets its own variant
         instead of an appended hedge, so no language has to glue two sentences together. */
      const range = (this.weightRanges || {})[data.breed];
      if (!range) {
        return tr('weight_unknown', 'We do not hold a reference weight for {breed}, so this assessment rests on the rib, waist and belly answers alone.', { breed: data.breed });
      }

      const [min, max, confidence] = range;
      const weight = Number(data.weight);
      const age = Number(data.age);
      const values = { breed: data.breed, min, max, weight };

      if (Number.isFinite(age) && age < 1.5) {
        return tr('weight_puppy', 'An adult {breed} usually weighs {min} to {max} kg. Your dog is still growing, so that range is not applied here - puppies are legitimately below it.', values);
      }

      const estimate = confidence === 'medium';

      // Eleven times the top of the range is not a weight, it is a mistake. Saying so is more
      // use to the owner than any verdict, and it is the honest reading: a 30 kg Chihuahua is
      // either a typo, a different dog, or an emergency that no web form should be triaging.
      if (weight > max * 3 || weight < min * 0.35) {
        return tr('weight_far_outside', 'An adult {breed} usually weighs {min} to {max} kg, so {weight} kg is a long way outside it. Please check the breed and the weight you entered. If both are right, this is one to take to your vet rather than to a calculator.', values);
      }

      if (weight > max) {
        return estimate
          ? tr('weight_above_estimate', 'An adult {breed} usually weighs {min} to {max} kg. At {weight} kg your dog is above that range. This breed is a cross, so its reference range is an estimate and carries less weight here.', values)
          : tr('weight_above', 'An adult {breed} usually weighs {min} to {max} kg. At {weight} kg your dog is above that range.', values);
      }

      if (weight < min) {
        return estimate
          ? tr('weight_below_estimate', 'An adult {breed} usually weighs {min} to {max} kg. At {weight} kg your dog is below that range. This breed is a cross, so its reference range is an estimate and carries less weight here.', values)
          : tr('weight_below', 'An adult {breed} usually weighs {min} to {max} kg. At {weight} kg your dog is below that range.', values);
      }

      return tr('weight_inside', 'An adult {breed} usually weighs {min} to {max} kg, and at {weight} kg your dog is inside that range.', values);
    }

    getBreedWeightAdjustment(data) {
      const range = (this.weightRanges || {})[data.breed];
      if (!range) return 0;

      const weight = Number(data.weight);
      const [min, max, confidence] = range;
      if (!Number.isFinite(weight) || !min || !max) return 0;

      const age = Number(data.age);
      if (Number.isFinite(age) && age < 1.5) return 0;

      let steps = 0;
      if (weight > max) {
        const over = weight / max;
        steps = over > 1.6 ? 3 : over > 1.25 ? 2 : 1;
      } else if (weight < min) {
        const under = weight / min;
        steps = under < 0.6 ? -3 : under < 0.8 ? -2 : -1;
      }

      // An extreme mismatch is not a fat dog, it is a typo or a different dog, and the hedge for
      // an estimated range must not swallow it: a Cavapoo entered at 45 kg against a 4-11 kg
      // range came back "Ideal" while the halving below was allowed to apply to it.
      const extreme = weight > max * 3 || weight < min * 0.35;

      if (confidence === 'medium' && !extreme) {
        steps = steps > 0 ? Math.floor(steps / 2) : Math.ceil(steps / 2);
      }

      return steps;
    }

    validate(data) {
      /* I18N-06 (#163): the field names are the form's own labels; kg and cm stay symbols. */
      const limits = [
        ['weight', tr('weight', 'Weight'), 0.5, 120, 'kg'],
        ['height', tr('height', 'Height at shoulder'), 5, 120, 'cm'],
        ['length', tr('length', 'Body length'), 10, 200, 'cm'],
        ['age', tr('age', 'Age'), 0, 30, tr('unit_years', 'years')],
      ];

      for (const [key, label, min, max, unit] of limits) {
        const value = Number(data[key]);
        if (!Number.isFinite(value) || value < min || value > max) {
          return tr('error_range', '{label} should be between {min} and {max} {unit}. Please check the figure you entered.', { label, min, max, unit });
        }
      }

      return '';
    }

    getCategoryContent(score) {
      /* I18N-06 (#163): title and summary are translated. `category` stays an English identifier: the
         report turns it into a whole translated sentence (buildReport), and the GAP3-03 tests compare it. */
      if (score <= 2) {
        return {
          category: 'Very underweight',
          title: tr('title_very_underweight', 'Very low BMI assessment'),
          summary:
            tr('summary_very_underweight', 'Your dog appears to be well below an ideal body range. The submitted details suggest low body reserves, which may require a closer look at diet, appetite, digestion, and overall health.')
        };
      }

      if (score === 3) {
        return {
          category: 'Underweight',
          title: tr('title_underweight', 'Below ideal BMI assessment'),
          summary:
            tr('summary_underweight', 'Your dog may be slightly below ideal condition. This can happen in very active dogs, young growing dogs, or dogs with insufficient calorie intake.')
        };
      }

      if (score >= 4 && score <= 5) {
        return {
          category: 'Ideal',
          title: tr('title_ideal', 'Ideal BMI assessment'),
          summary:
            tr('summary_ideal', 'Your dog appears to be within a healthy body range. The goal is to keep ribs easy to feel, waist visible from above, and abdominal tuck present from the side.')
        };
      }

      if (score === 6) {
        return {
          category: 'Slightly overweight',
          title: tr('title_slightly_overweight', 'Slightly above ideal BMI assessment'),
          summary:
            tr('summary_slightly_overweight', 'Your dog may be starting to carry extra body fat. This is a good stage to make small adjustments before weight becomes harder to manage.')
        };
      }

      if (score === 7) {
        return {
          category: 'Overweight',
          title: tr('title_overweight', 'Above ideal BMI assessment'),
          summary:
            tr('summary_overweight', 'Your dog appears to be above ideal range. Ribs may be harder to feel, the waist may be less defined, and movement or endurance may be affected over time.')
        };
      }

      return {
        category: 'Obese',
        title: tr('title_obese', 'High BMI assessment'),
        summary:
          tr('summary_obese', 'Your dog may be significantly above ideal range. A structured, gradual weight-management plan is recommended, ideally with veterinary guidance.')
      };
    }

    getBreedSizeGroup(breed, breedsData) {
      const breedKey = this.normalizeKey(breed);
      if (!breedKey) return '';

      for (const [size, breeds] of Object.entries(breedsData || {})) {
        const found = (breeds || []).some((item) => {
          return this.normalizeKey(item) === breedKey;
        });

        if (found) return size;
      }

      return '';
    }

    getBreedSizeLabel(size) {
      const labels = {
        small: 'small',
        medium: 'medium-sized',
        large: 'large',
        extra_large: 'extra-large'
      };

      return labels[size] || '';
    }

    getBreedInsight(data) {
      /* I18N-06 (#163): translated. The last line stays English: every breed the list offers has a size. */
      if (data.breedSize === 'small') {
        return tr('insight_small', 'This breed is usually considered small. Small dogs can gain weight quickly because even small treat portions can make a noticeable difference.');
      }

      if (data.breedSize === 'medium') {
        return tr('insight_medium', 'This breed is usually considered medium-sized. BMI should still be interpreted together with ribs, waist, abdominal tuck, and activity level rather than weight alone.');
      }

      if (data.breedSize === 'large') {
        return tr('insight_large', 'This breed is usually considered large. Keeping large dogs lean helps reduce pressure on joints, mobility, stamina, and long-term comfort.');
      }

      if (data.breedSize === 'extra_large') {
        return tr('insight_extra_large', 'This breed is usually considered extra-large. Weight control is especially important because extra body fat can add stress to joints, spine, hips, elbows, and movement.');
      }

      return 'Breed type can affect ideal weight range, muscle mass, chest depth, coat thickness, and natural body shape.';
    }

    getMeasurementInsight(data) {
      const bmiIndex = Number(this.calculateBmiIndex(data));
      if (!bmiIndex) return '';

      /* I18N-06 (#163): the five readings of the index are translated. */
      if (bmiIndex < 30) {
        return tr('measure_very_light', 'The entered measurements suggest a very light body mass relative to height and body length.');
      }

      if (bmiIndex < 36) {
        return tr('measure_lean', 'The entered measurements suggest a lean body build relative to height and body length.');
      }

      if (bmiIndex > 100) {
        return tr('measure_high', 'The entered measurements suggest a high body mass relative to height and body length.');
      }

      if (bmiIndex > 76) {
        return tr('measure_heavier', 'The entered measurements suggest a slightly heavier build relative to height and body length.');
      }

      return tr('measure_balanced', 'The entered measurements appear broadly consistent with a balanced adult dog body shape.');
    }

    buildReport(data, assessmentScore, category, bmiIndex) {
      /* I18N-06 (#163): every line a visitor can reach is translated, each one a whole sentence, so no
         language has to assemble one from English parts (the a/an article, the size words). */
      const report = [];

      if (bmiIndex) {
        // GAP3-03 (#219): a reader who knows the human BMI scale reads 45 as starvation and 75 as
        // fatal. This number is weight divided by height times length and sits on a different
        // scale entirely, so it is named and bounded rather than left to be misread.
        report.push(
          tr('body_index', 'Body index: {value} on our own canine scale, where most dogs fall between 40 and 110. This is not a human BMI and the two cannot be compared.', { value: bmiIndex })
        );
      }

      /* I18N-06 (#163): the assessment line, one whole sentence per result; so is the breed line. */
      const assessments = {
        'Very underweight': tr('assessment_very_underweight', 'BMI assessment: Very underweight.'),
        Underweight: tr('assessment_underweight', 'BMI assessment: Underweight.'),
        Ideal: tr('assessment_ideal', 'BMI assessment: Ideal.'),
        'Slightly overweight': tr('assessment_slightly_overweight', 'BMI assessment: Slightly overweight.'),
        Overweight: tr('assessment_overweight', 'BMI assessment: Overweight.'),
        Obese: tr('assessment_obese', 'BMI assessment: Obese.')
      };
      report.push(assessments[category]);

      if (data.breed) {
        const sizeLabel = this.getBreedSizeLabel(data.breedSize);

        if (sizeLabel) {
          const entered = {
            small: tr('breed_entered_small', 'Breed entered: {breed}, a small breed.', { breed: data.breed }),
            medium: tr('breed_entered_medium', 'Breed entered: {breed}, a medium-sized breed.', { breed: data.breed }),
            large: tr('breed_entered_large', 'Breed entered: {breed}, a large breed.', { breed: data.breed }),
            extra_large: tr('breed_entered_extra_large', 'Breed entered: {breed}, an extra-large breed.', { breed: data.breed })
          };
          report.push(entered[data.breedSize]);
        } else {
          report.push(`Breed entered: ${data.breed}. Breed size was not found in the current breed list.`);
        }

        // Outside the size branch on purpose: the two lists are separate files, and a breed
        // missing from one is not necessarily missing from the other. Whichever is absent,
        // the report should still say what was and was not used.
        report.push(this.getBreedWeightNote(data));
        report.push(this.getBreedInsight(data));
      }

      if (data.weight && data.height && data.length) {
        /* I18N-06 (#163): the measurements, age and answer lines below are translated too. */
        report.push(tr('measurements', 'Entered measurements: {weight} kg, {height} cm shoulder height, {length} cm body length.', { weight: data.weight, height: data.height, length: data.length }));
        report.push(this.getMeasurementInsight(data));
      }

      if (data.age <= 1) {
        report.push(tr('age_young', 'Because your dog is still young, this result should be interpreted together with growth stage, breed size, and expected adult development.'));
      } else if (data.age >= 7) {
        report.push(tr('age_senior', 'Because your dog is senior or approaching senior age, it is worth monitoring weight, muscle condition, mobility, joints, and daily comfort more closely.'));
      } else {
        report.push(tr('age_adult', 'At this adult age, maintaining a stable body range is usually easier than correcting weight gain later.'));
      }

      if (data.ribs === 'very_visible') {
        report.push(tr('note_ribs_very_visible', 'Ribs being clearly visible strongly suggests low fat coverage.'));
      } else if (data.ribs === 'easy_to_feel') {
        report.push(tr('note_ribs_easy_to_feel', 'Ribs that are easy to feel usually indicate good fat coverage when the waist and abdominal tuck also look healthy.'));
      } else if (data.ribs === 'slightly_padded') {
        report.push(tr('note_ribs_slightly_padded', 'Slightly padded ribs can still be normal, especially if the waist remains visible and the abdominal tuck is present.'));
      } else if (data.ribs === 'hard_to_feel') {
        report.push(tr('note_ribs_hard_to_feel', 'Ribs that are hard to feel suggest extra fat coverage and may be an early sign of weight gain.'));
      } else if (data.ribs === 'not_feelable') {
        report.push(tr('note_ribs_not_feelable', 'Ribs that are difficult or impossible to feel are usually a strong sign of excess body fat.'));
      }

      if (data.waist === 'very_tucked') {
        report.push(tr('note_waist_very_tucked', 'A very narrow waist may suggest a lean or underweight condition, especially when combined with visible ribs.'));
      } else if (data.waist === 'visible') {
        report.push(tr('note_waist_visible', 'A visible waist from above is one of the strongest signs of a healthy body range.'));
      } else if (data.waist === 'slight') {
        report.push(tr('note_waist_slight', 'A slight waist may be acceptable, but it can also suggest early weight gain depending on rib coverage.'));
      } else if (data.waist === 'none') {
        report.push(tr('note_waist_none', 'No clear waist usually suggests excess fat around the trunk.'));
      } else if (data.waist === 'rounded') {
        report.push(tr('note_waist_rounded', 'A rounded body shape usually points toward a higher BMI assessment.'));
      }

      if (data.belly === 'strong_tuck') {
        report.push(tr('note_belly_strong_tuck', 'A strong abdominal tuck can be normal in athletic dogs but may suggest low reserves if ribs are very visible.'));
      } else if (data.belly === 'normal_tuck') {
        report.push(tr('note_belly_normal_tuck', 'A normal abdominal tuck supports a healthy body range.'));
      } else if (data.belly === 'minimal_tuck') {
        report.push(tr('note_belly_minimal_tuck', 'A minimal abdominal tuck can be an early sign that extra fat is starting to build.'));
      } else if (data.belly === 'straight') {
        report.push(tr('note_belly_straight', 'A straight underside usually suggests reduced abdominal tuck and possible extra fat.'));
      } else if (data.belly === 'hanging') {
        report.push(tr('note_belly_hanging', 'A hanging belly or heavy underside usually suggests excess fat and reduced body balance.'));
      }

      if (data.activity === 'low') {
        report.push(tr('note_activity_low', 'Low activity means food portions and treats should be managed carefully to avoid gradual weight gain.'));
      } else if (data.activity === 'normal') {
        report.push(tr('note_activity_normal', 'Average activity is suitable for maintenance if food intake, treats, and exercise stay consistent.'));
      } else if (data.activity === 'high') {
        report.push(tr('note_activity_high', 'High activity can increase calorie and recovery needs, but it does not always prevent weight gain if treats or portions are too high.'));
      }

      /* I18N-06 (#163): the closing disclaimer is translated. */
      report.push(tr('disclaimer', 'This result is a general wellness guide and should not replace veterinary advice.'));

      return report.filter(Boolean);
    }

    buildRecommendations(data, assessmentScore) {
      const breed = String(data.breed || 'your dog').trim() || 'your dog';

      /* I18N-06 (#163): every recommendation is translated; {breed} is the breed as the owner picked it. */
      if (assessmentScore <= 2) {
        return tr('advice_very_underweight', '{breed} appears to be very lean based on the submitted details. Review daily calorie intake, appetite, stool quality, recent illness, parasite prevention, and unexplained weight loss. Avoid rapid overfeeding; gradual nutritional support and veterinary advice are recommended.', { breed });
      }

      if (assessmentScore === 3) {
        return tr('advice_underweight', '{breed} may benefit from slightly improved nutritional support. Consider reviewing food quantity, meal frequency, protein quality, activity level, and whether weight has been stable. If the dog is very active, calorie needs may simply be higher.', { breed });
      }

      if (assessmentScore >= 4 && assessmentScore <= 5) {
        return tr('advice_ideal', '{breed} appears to be in a healthy range. Maintain the current routine, keep treats controlled, check ribs and waist monthly, and track weight trends.', { breed });
      }

      if (assessmentScore === 6) {
        return tr('advice_slightly_overweight', '{breed} may be slightly above ideal range. Start with small changes: reduce treats, avoid table scraps, measure meals more accurately, and add a little more daily movement.', { breed });
      }

      if (assessmentScore === 7) {
        return tr('advice_overweight', '{breed} is likely carrying extra weight. Focus on controlled portions, lower-calorie treats, structured walks, play sessions, and weekly weight tracking.', { breed });
      }

      return tr('advice_obese', '{breed} may need a structured weight-management plan. Use measured portions, reduce calorie-dense extras, choose low-impact activity, track progress consistently, and speak with a veterinarian before making major diet changes.', { breed });
    }

    renderResult(result) {
      if (this.resultTitle) {
        this.resultTitle.textContent = result.title;
      }

      if (this.scoreEl) {
        this.scoreEl.textContent = result.bmiIndex || result.category;
      }

      if (this.resultText) {
        this.resultText.textContent = result.summary;
      }

      if (this.reportList) {
        this.reportList.innerHTML = result.report
          .map((item) => `<li>${this.escapeHtml(item)}</li>`)
          .join('');
      }

      if (this.recommendations) {
        this.recommendations.textContent = result.recommendations;
      }
    }

    openModal() {
      if (!this.modal) return;

      this.modal.classList.add('is-open');
      this.modal.setAttribute('aria-hidden', 'false');
      document.body.classList.add('modal-open');
      document.addEventListener('keydown', this.onKeydown);
    }

    closeModal() {
      if (!this.modal) return;

      this.modal.classList.remove('is-open');
      this.modal.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('modal-open');
      document.removeEventListener('keydown', this.onKeydown);
    }

    onKeydown(event) {
      if (event.key === 'Escape') {
        this.closeModal();
      }
    }

    normalizeKey(value) {
      return String(value || '')
        .trim()
        .toLowerCase()
        .replaceAll('ã¶', 'ö')
        .replaceAll('ã©', 'é')
        .replaceAll('â€™', '’')
        .replaceAll('â€˜', '‘')
        .replaceAll('â€“', '–')
        .replaceAll('â€”', '—')
        .replace(/\s+/g, ' ');
    }

    escapeHtml(value) {
      return String(value || '')
        .replaceAll('&', '&amp;')
        .replaceAll('<', '&lt;')
        .replaceAll('>', '&gt;')
        .replaceAll('"', '&quot;')
        .replaceAll("'", '&#039;');
    }
  }

  if (!customElements.get('s-bmi-calculator')) {
    customElements.define('s-bmi-calculator', SBmiCalculator);
  }
})();
